import {BYNDER_COUNTRY_OPTIONS, BYNDER_META, metaKey} from '@/assets/bynder/metaproperties'
import {useCountries} from '@/hooks/useCountries'
import {generateDamTokenService} from '@/services/bynderService'
import Bynder from '@/utils/bynderSdk'
import {useMutation} from '@tanstack/react-query'

const BYNDER_TOKEN_KEY = 'bynderToken'
const POLLING_INTERVAL = 2000
const MAX_POLLING_ATTEMPTS = 60

/**
 * Hook for Bynder DAM (Digital Asset Management) integration
 *
 * This hook provides methods to:
 * - Upload files to Bynder
 * - Get assets by tag
 * - Get media info
 */
export function useBynder() {
  const {country} = useCountries()
  const {mutateAsync: generateDamToken} = useMutation({
    mutationFn: generateDamTokenService,
  })

  async function makeSdkToken() {
    async function fetchAuthToken() {
      try {
        const token = await generateDamToken()
        if (!token) throw new Error("[DAM] Could not retrieve Bynder's access token!")
        return token
      } catch (error) {
        throw new Error(`Bynder token generation failed: ${error}`)
      }
    }

    const cachedToken = localStorage.getItem(BYNDER_TOKEN_KEY)
    const {expirationDate, accessToken: cachedAccessToken} = cachedToken
      ? JSON.parse(cachedToken)
      : {expirationDate: undefined, accessToken: undefined}

    const isExpired = () => !expirationDate || new Date() > new Date(expirationDate)

    let accessToken = cachedAccessToken
    if (!accessToken || isExpired()) {
      const {expires_in, access_token: newAccessToken} = await fetchAuthToken()
      const newExpirationDate = new Date(Date.now() + expires_in * 1000)
      newExpirationDate.setMinutes(newExpirationDate.getMinutes() - 5) // Subtract 5 minutes for safety

      localStorage.setItem(
        BYNDER_TOKEN_KEY,
        JSON.stringify({
          accessToken: newAccessToken,
          expirationDate: newExpirationDate.toISOString(),
        })
      )
      accessToken = newAccessToken
    }

    return {
      expired: isExpired,
      refresh: makeSdkToken,
      token: {
        access_token: accessToken,
      },
    }
  }

  const bynderFactory = async () => {
    // DEV ONLY: Allow using permanent token directly (not secure for production!)
    const permanentToken = import.meta.env.VITE_BYNDER_PERMANENT_TOKEN

    if (permanentToken) {
      console.warn(
        '⚠️ Using Bynder permanent token directly. This is for DEVELOPMENT ONLY and should NEVER be used in production!'
      )
      return new Bynder({
        baseURL: import.meta.env.VITE_BYNDER_BASE_URL,
        permanentToken,
      })
    }

    // Production: Use OAuth tokens from backend
    const token = await makeSdkToken()
    return new Bynder({
      baseURL: import.meta.env.VITE_BYNDER_BASE_URL,
      token,
    })
  }

  async function upload({
    file,
    title,
    entityId = '',
    updatePercentage,
    abortController,
    tags,
  }: {
    file: File
    title: string
    entityId?: string
    updatePercentage: (percentage: number) => void
    abortController?: AbortController
    tags?: string
  }) {
    const now = new Date()
    const twentyYearsFromNow = new Date(now)
    twentyYearsFromNow.setFullYear(twentyYearsFromNow.getFullYear() + 20)

    const bynder = await bynderFactory()
    const resultAssetUpload = await bynder.uploadFile(
      {
        filename: file.name,
        body: file,
        data: {
          brandId: import.meta.env.VITE_BYNDER_BRAND_ID,
          name: title,
          ...(tags ? {tags} : {}),

          // select
          [metaKey(BYNDER_META.sector.id)]: BYNDER_META.sector.optionId,
          [metaKey(BYNDER_META.country.id)]: (country && BYNDER_COUNTRY_OPTIONS[country]) ?? '',
          [metaKey(BYNDER_META.businessFunction.id)]: BYNDER_META.businessFunction.optionId,
          [metaKey(BYNDER_META.rightsAvailable.id)]: BYNDER_META.rightsAvailable.optionId,
          [metaKey(BYNDER_META.assetPurpose.id)]: BYNDER_META.assetPurpose.optionId,
          [metaKey(BYNDER_META.assetType.id)]: BYNDER_META.assetType.optionId,
          [metaKey(BYNDER_META.auditStatus.id)]: BYNDER_META.auditStatus.optionId,

          // select2
          [metaKey(BYNDER_META.brand.id)]: BYNDER_META.brand.optionId,
          [metaKey(BYNDER_META.subBrand.id)]: BYNDER_META.subBrand.optionId,
          [metaKey(BYNDER_META.globalCampaign.id)]: BYNDER_META.globalCampaign.optionId,
          [metaKey(BYNDER_META.agencyName.id)]: BYNDER_META.agencyName.optionId,

          // text / longtext
          [metaKey(BYNDER_META.keyContactDetails.id)]: 'dl-journeyoptimizerteam@pepsico.com',
          [metaKey(BYNDER_META.usageTerms.id)]:
            "Please contact the indicated Key Contact to know more about the asset's commercial and usage rights. This asset is for internal use only. For any usage request please contact PEPLoyalty@pepsico.com to discuss any specific term for your sector.",

          // date
          [metaKey(BYNDER_META.inMarketDate.id)]: now.toLocaleDateString('en-US'),
          [metaKey(BYNDER_META.expiryDate.id)]: twentyYearsFromNow.toLocaleDateString('en-US'),

          // other
          [metaKey(BYNDER_META.integrationSourceId.id)]: entityId,
          [metaKey(BYNDER_META.assetSource.id)]: 'CCT Integration',
        },
      },
      ({chunks = 1, chunksUploaded = 0}) => {
        const percentage = (chunksUploaded * 100) / chunks
        updatePercentage(Math.floor(percentage))
      },
      abortController
    )

    if ((resultAssetUpload as Record<string, unknown>).statuscode) {
      throw new Error(
        `An error occurred during the Bynder upload flow of ${file.name}. Bynder API response: ${JSON.stringify(
          resultAssetUpload,
          null,
          2
        )}`
      )
    }

    const response = await waitForSaveUploadedFileSync(resultAssetUpload)
    return response
  }

  const waitForSaveUploadedFileSync = async ({mediaid}: {mediaid: string}) => {
    return new Promise((resolve, reject) => {
      pollMediaInfo(mediaid, resolve, reject, MAX_POLLING_ATTEMPTS)
    })
  }

  const pollMediaInfo = async (
    mediaid: string,
    resolve: (value: unknown) => void,
    reject: (reason?: unknown) => void,
    retry: number
  ) => {
    if (retry < 0) {
      reject('Image upload sync timeout!')
      return
    }

    const mediaInfoResult = await getMediaInfo(mediaid)
    if ((mediaInfoResult as Record<string, unknown>).original) {
      setTimeout(() => resolve(mediaInfoResult), 0)
      return
    }

    setTimeout(() => pollMediaInfo(mediaid, resolve, reject, retry - 1), POLLING_INTERVAL)
  }

  async function getAssetsByTag(tag = import.meta.env.VITE_PROGRAM || 'CCT') {
    const bynder = await bynderFactory()
    return bynder.getAssetsByTag(tag)
  }

  async function getMediaInfo(id: string) {
    const bynder = await bynderFactory()
    return bynder.getMediaInfo({id})
  }

  return {
    upload,
    getAssetsByTag,
    getMediaInfo,
  }
}
